import {DASH} from './format'
import {normalizeText} from './workerAccounts'

/**
 * Phần tính toán thuần của màn Nhập chấm công (FAC-26417 FR-001 bước 8).
 *
 * Tách khỏi component vì **trong repo này không file test nào import một component nào** — mọi logic
 * nằm trong `.js` của màn hình đều có mutation score 0 theo cấu tạo. Cùng lý do đã ghi cho
 * `helpers/actualHours.js`.
 *
 * Service đã trả sẵn `labels` cho từng trạng thái; ở đây chỉ lo THỨ TỰ, phần trăm và cách gom nhóm —
 * tức những thứ quyết định người dùng đọc bảng này ra cái gì.
 */

/**
 * Thứ tự hiện các nhóm bị loại, cố ý KHÔNG theo số lượng.
 *
 * Xếp theo "người dùng làm được gì với nó": hai nhóm đầu sửa được bằng cách nhập tay hoặc bổ sung
 * bản đồ tài khoản, nhóm sau là hiện trạng của bảng HR. Xếp theo số lượng thì `leave_code` (nghỉ
 * phép, nghỉ lễ — 162 dòng trong file tháng 9) luôn đứng đầu và đẩy hai nhóm hành động được xuống
 * dưới màn hình.
 */
export const SKIP_ORDER = ['no_account', 'ambiguous_account', 'invalid', 'future', 'leave_code', 'no_entry']

/** Nhóm nào đáng tô đỏ: sai cấu hình, sửa được. Các nhóm còn lại là hiện trạng dữ liệu, tô vàng. */
export const SKIP_TONE = {
    no_account: 'error',
    ambiguous_account: 'error',
    invalid: 'error',
    future: 'warning',
    leave_code: 'warning',
    no_entry: 'warning',
}

/** `undefined` và 0 đều ra `0%`; tổng 0 không được chia. */
export const shareOf = (count, total) => (total > 0 ? Math.round(((count || 0) / total) * 100) : 0)

/**
 * Các nhóm dòng bị loại, đã sắp xếp và kèm tỷ lệ.
 *
 * Nhóm có 0 dòng bị bỏ hẳn: in "Ca qua đêm: 0" cho mọi file không có ca đêm là dạy người dùng bỏ
 * qua cả khối thông tin này.
 */
export const skipGroupsOf = (preview) => {
    const byStatus = (preview && preview.summary && preview.summary.byStatus) || {}
    const labels = (preview && preview.labels) || {}
    const total = (preview && preview.readRows) || 0
    const known = SKIP_ORDER.filter((status) => byStatus[status] > 0)
    /*
     * Trạng thái service trả về mà FE chưa biết tên vẫn phải hiện — thêm một lý do loại ở service mà
     * màn hình im lặng nuốt thì tổng các nhóm không bằng số dòng bị loại, và không ai tìm ra phần
     * chênh đi đâu.
     */
    const extra = Object.keys(byStatus).filter(
        (status) => status !== 'ok' && byStatus[status] > 0 && SKIP_ORDER.indexOf(status) < 0
    )

    return [...known, ...extra.sort()].map((status) => ({
        status,
        label: labels[status] || status,
        count: byStatus[status],
        share: shareOf(byStatus[status], total),
        tone: SKIP_TONE[status] || 'warning',
    }))
}

/**
 * Khoá DUY NHẤT của một dòng xem trước: mã nhân viên + ngày.
 *
 * ⚠️ **KHÔNG dùng `row.row` làm khoá.** Từ khi nguồn đổi sang bảng chấm công tổng hợp, `row` là số
 *    hàng Excel của NGƯỜI chứ không phải của dòng xem trước: một người sinh ra tới 31 dòng mang y
 *    hệt một số hàng (file tháng 9 thật: 9 dòng cùng mang số 11). Nguồn cũ — máy quét thẻ — một
 *    dòng đúng là một người-một ngày, nên khoá đó từng đúng và hỏng im lặng lúc đổi nguồn.
 *
 * Hậu quả đã gặp: `rowKey` trùng nên React ghép nhầm ô nhập sang dòng khác (gõ xong bị trả về số
 * cũ, trông như "không sửa được"), và đổi bộ lọc thì bảng giữ nguyên dòng đang hiện.
 *
 * Cặp (mã, ngày) thì parser bảo đảm duy nhất — nó dọn trùng ngay lúc đọc — và đây cũng đúng khoá
 * service dùng để khớp `overrides`. Một khoá cho cả bảng, ô sửa tay lẫn payload.
 */
export const rowKeyOf = (row) => (row ? `${row.employeeCode}|${row.workDate}` : '')

/** Dòng sẽ GHI ĐÈ lên giá trị đang có. `currentHours === null` nghĩa là ô còn trống. */
export const overwriteRowsOf = (rows) =>
    (rows || []).filter((row) => row.status === 'ok' && row.currentHours !== null && row.currentHours !== undefined)

/**
 * Lọc bảng xem trước theo chế độ xem.
 *
 * `all` giữ nguyên thứ tự service trả về — đó là thứ tự dòng trong file, thứ duy nhất đối chiếu
 * được với Excel đang mở bên cạnh.
 */
export const filterRows = (rows, mode) => {
    if (mode === 'importable') return (rows || []).filter((row) => row.status === 'ok')
    if (mode === 'overwrite') return overwriteRowsOf(rows)
    if (mode === 'skipped') return (rows || []).filter((row) => row.status !== 'ok')
    if (mode === 'warning') return (rows || []).filter((row) => (row.warnings || []).length > 0)
    /*
     * Mã chưa có tài khoản tại ngày của dòng là nhóm DUY NHẤT người dùng gỡ được mà không cần hỏi
     * HR: đi bổ sung bản đồ tài khoản rồi nhập lại chính file này. Nó cũng là nhóm không sửa tay
     * được (không biết ghi vào tài khoản nào), nên lọc ra để chép danh sách mã là việc thật.
     */
    if (mode === 'no_account') return (rows || []).filter((row) => row.status === 'no_account')

    return rows || []
}

/**
 * Nội dung MỘT ô ca, đúng như nó nằm trong file: số giờ, hoặc mã nghỉ nguyên văn, hoặc `—`.
 *
 * Bản xem trước bày hai cột **Ca ngày** · **Ca đêm** khớp từng ô với file Excel, thay cho một cột
 * gộp kiểu `3 + 5`. Gộp thì `8` một mình không nói được ca nào, và phải thêm chú thích "(có ca
 * đêm)" để cứu — tức là cột đó không tự đọc được.
 *
 * ⚠️ Mã nghỉ hiện ở ĐÚNG ô nó nằm (`dayCode`/`nightCode`, không phải một mảng gộp). `NL` ở ô Đêm
 *    nói chuyện khác hẳn `NL` ở ô Ngày, và người soát cần phân biệt "nghỉ phép" với "HR quên
 *    điền" — hai thứ trông giống hệt nhau nếu ô chỉ hiện gạch ngang.
 *
 * ⚠️ `0` là một giá trị THẬT, phải hiện ra số 0 chứ không rơi vào nhánh `—`: ô trống nghĩa là HR
 *    không điền, `0` nghĩa là ca đó làm 0 giờ.
 */
export const shiftCellTextOf = (row, shift) => {
    if (!row) return DASH

    const code = shift === 'night' ? row.nightCode : row.dayCode

    if (code) return code

    const hours = shift === 'night' ? row.nightHours : row.dayHours

    if (hours === null || hours === undefined) return DASH

    return String(hours)
}

/**
 * Câu tóm tắt một dòng cảnh báo, hoặc `null` khi dòng bình thường.
 *
 * Giữ ở đây chứ không nhúng trong JSX vì nó là LUẬT đọc số, không phải cách bày: ngày dài có thể là
 * tăng ca thật, nên câu chữ phải để ngỏ hai khả năng chứ không kết luận là lỗi.
 */
export const warningTextOf = (row) => {
    const warnings = (row && row.warnings) || []
    const parts = []

    if (warnings.indexOf('over_standard') >= 0) parts.push('Vượt 8 giờ công — hiếm, HR nhập nhầm?')
    if (warnings.indexOf('short_day') >= 0) parts.push('Dưới 4 giờ — nửa ca hay HR nhập thiếu?')

    return parts.length > 0 ? parts.join(' · ') : null
}

/**
 * Nhập được hay không, kèm LÝ DO khi không.
 *
 * Trả lý do chứ không trả mỗi `false`: nút Nhập bị khoá mà không nói vì sao là thứ người dùng không
 * tự gỡ được, và họ sẽ gửi lại đúng file đó lần nữa.
 */
export const importBlockerOf = (preview) => {
    if (!preview) return 'Chưa chọn file.'

    const importable = (preview.summary && preview.summary.importable) || 0

    if (importable > 0) return null

    if ((preview.readRows || 0) === 0) {
        return 'File không đọc được dòng nào — kiểm tra đúng bảng chấm công tổng hợp (sheet "CC") chưa.'
    }

    return 'Không dòng nào nhập được. Xem cột Kết quả để biết lý do từng dòng.'
}

/**
 * Tìm trong bảng xem trước chấm công.
 *
 * Bảng này tới 1.500 dòng — không có ô tìm thì xem lại một người cụ thể nghĩa là lật 75 trang.
 * Tìm trên mã nhân viên, tên theo file, tài khoản và NGÀY: `02/07` không khớp gì vì dữ liệu là
 * `2026-07-02`, nên ngày phải gõ theo đúng dạng đang hiện trên bảng.
 *
 * Dùng chung `normalizeText` với bản đồ tài khoản để gõ `tu` vẫn ra "Nguyễn Trọng Tú" — tên theo
 * file chấm công có dòng còn dấu, có dòng mất dấu (`nvthong`, `tran manh an`), nên so khớp nguyên
 * văn là hộp tìm kiếm ăn may.
 */
export const searchTimesheetRows = (rows, term) => {
    const needle = normalizeText(term)

    if (needle === '') return rows || []

    return (rows || []).filter((row) => [row.employeeCode, row.fullName, row.actor, row.workDate]
        .some((field) => normalizeText(field).indexOf(needle) >= 0))
}

/**
 * Trạng thái một dòng SAU khi người dùng sửa tay số giờ.
 *
 * ⚠️ Phải khớp từng bước với `TimesheetImportService.resolve` phía service, kể cả THỨ TỰ kiểm.
 *    Lệch một bước là màn hình đếm "nhập được 900 dòng" rồi service ghi 880 và không ai biết 20
 *    dòng kia đi đâu. Bản service là bản quyết định; bản này chỉ để màn hình khỏi phải hỏi lại sau
 *    mỗi lần gõ.
 *
 */
export const statusWithEdit = (row, hours) => {
    // Ô bị xoá về trống quay lại ĐÚNG trạng thái gốc — gõ vào rồi xoá đi phải trả dòng về y như cũ,
    // không để lại một trạng thái thứ ba mà không nút lọc nào đếm tới.
    if (hours === null || hours === undefined) return row.status

    if (row.status === 'future') return 'future'
    if ((row.actors || []).length === 0) return 'no_account'
    if ((row.actors || []).length > 1) return 'ambiguous_account'

    return 'ok'
}

/**
 * Dòng này có sửa tay được số giờ không.
 *
 * Chỉ khoá những dòng mà gõ một con số KHÔNG giải quyết được gì: không biết ghi vào tài khoản nào
 * (chưa có tài khoản, hoặc có hai), và ngày tương lai (service từ chối ghi — BR-1.7). Dòng mã nghỉ
 * và dòng số lạ thì sửa được: đó là "bảng HR không nói" chứ không phải "không có câu trả lời", và
 * người quản lý ca thường BIẾT con số thật.
 */
export const isEditable = (row) =>
    !!row && row.status !== 'future' && (row.actors || []).length === 1

/**
 * Giờ người dùng THẬT SỰ đổi cho một dòng, hoặc `undefined` khi dòng chưa đổi gì.
 *
 * ⚠️ Vị từ "đã sửa hay chưa" chỉ được có MỘT bản. Trước đây bảng và payload mỗi bên tự quyết: payload
 *    bỏ qua dòng gõ lại đúng con số của file, còn bảng vẫn treo cờ "Sửa tay" cho nó — hai thứ nói
 *    hai điều khác nhau về cùng một dòng, và cái mất đi là dòng cảnh báo vốn phải hiện ở đó.
 *
 * Ba trường hợp đều là "chưa đổi gì": không chạm tới ô, xoá ô về trống, và gõ lại đúng con số cũ.
 */
export const editedHoursOf = (row, edits) => {
    const key = rowKeyOf(row)

    if (!row || !Object.prototype.hasOwnProperty.call(edits || {}, key)) return undefined

    const hours = edits[key]

    if (hours === null || hours === undefined) return undefined

    /*
     * ⚠️ KHÔNG còn quy đổi "có mặt = làm + giải lao". Con số HR đưa đã là giờ công ròng (chặn ở 8),
     *    nên thứ người dùng gõ và thứ ghi xuống DB là CÙNG MỘT đại lượng. Giữ lại phép cộng cũ là
     *    thổi mẫu số lên đúng bằng một bữa trưa cho mọi dòng sửa tay.
     */
    return hours === row.hours ? undefined : hours
}

/** Dòng đã hợp nhất giá trị người dùng gõ. Một chỗ sinh ra, để bảng và payload không lệch nhau. */
export const mergeEdits = (rows, edits) => (rows || []).map((row) => {
    const hours = editedHoursOf(row, edits)

    // Dòng chưa đổi gì giữ NGUYÊN tham chiếu cũ: tạo object mới cho mọi dòng là bảng 2.000 dòng vẽ
    // lại toàn bộ sau mỗi phím gõ.
    if (hours === undefined) return row

    /*
     * ⚠️ Sửa tay thì phần tách ca về `null`: người dùng gõ TỔNG mới, không ai gõ được phần đó chia
     *    cho hai ca thế nào. Giữ `3`/`5` bên cạnh một tổng mới là bảng tự mâu thuẫn — khớp đúng
     *    luật của service.
     */
    return {...row, hours, dayHours: null, nightHours: null, manual: true, status: statusWithEdit(row, hours)}
})

/** Payload `overrides` gửi lên service — CHỈ những dòng người dùng thật sự đổi giá trị. */
export const overridePayloadOf = (rows, edits) => (rows || [])
    .filter((row) => editedHoursOf(row, edits) !== undefined)
    .map((row) => ({
        employeeCode: row.employeeCode,
        workDate: row.workDate,
        hours: editedHoursOf(row, edits),
    }))
